import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@me8eon/icons";
import { useTranslation } from "@me8eon/translation";
import { aiConfig } from "../..";
import { ocrPrompt } from "../../ocr/ai/ocrprompt";
import { Message } from "../ai/ai.types";
import { openAiClientForOcr } from "../../ocr/ai/aiclientforocr";
import { EonUiTooltip } from "@eon-ui/eon-ui-components-react";

const Tesseract = require("tesseract.js");

interface Props {
  isResetImage?: boolean;
  resetChangeTrigger: (flag: boolean) => void;
  setSelectedQuestion?: (q: string) => void;
  selectedQuestion?: string;
  setSuggestedQuestions: (q: string[]) => void;
}

const ImageUploader: React.FC<Props> = ({
  isResetImage = false,
  resetChangeTrigger,
  setSuggestedQuestions,
  setSelectedQuestion,
}) => {
  const t = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageName, setImageName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);


  const handleIconClick = () => fileInputRef.current?.click();

  const preprocessImage = async (file: File): Promise<string> => {
    const img = new Image();
    const objectURL = URL.createObjectURL(file);

    return new Promise((resolve, reject) => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const scale = 3;

        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

        const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData?.data;
        if (!data || !ctx || !imageData) return reject("Canvas error");

        let total = 0;
        for (let i = 0; i < data.length; i += 4) {
          const gray = (data[i] + data[i + 1] + data[i + 2]) / 3;
          total += gray;
        }

        const avgGray = total / (data.length / 4);
        const threshold = avgGray * 0.9;

        for (let i = 0; i < data.length; i += 4) {
          const gray = (data[i] + data[i + 1] + data[i + 2]) / 3;
          const binary = gray < threshold ? 0 : 255;
          data[i] = data[i + 1] = data[i + 2] = binary;
        }

        ctx.putImageData(imageData, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = objectURL;
    });
  };

  const performOCR = async (file: File): Promise<string> => {
    const imageDataUrl = await preprocessImage(file);

    try {
      const result = await Tesseract.recognize(
        imageDataUrl,
        "eng+deu+swe+ron",
        {
          langPath: "https://raw.githubusercontent.com/tesseract-ocr/tessdata_fast/main/",
          gzip: false
        }
      );

      return result.data.text.trim();
    } catch (err: any) {
      console.error(" OCR Error:", err.message || err);
      throw err;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setError("Only JPG and PNG formats are allowed.");
      setImageName("No image selected");
      return;
    }

    setImageName(file.name);
    setError("");
    setLoading(true);
    setSuggestedQuestions([]);

    try {
      const extractedText = await performOCR(file);

      const messagesStep1: Message[] = [
        {
          role: "system",
          content: ocrPrompt.replace("<INSERT OCR EXTRACTED TEXT HERE>", extractedText),
        },
        { role: "user", content: extractedText },
      ];

      const ocrClient = openAiClientForOcr(aiConfig);
      const step1Response = await ocrClient(messagesStep1);
      const questions = step1Response?.[step1Response.length - 1]?.content?.trim();

      const parsedQuestions = questions
        ?.split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean)
        .map(q => q.replace(/^\s*[\d]+[.)-]?\s*/, "").trim());

      setSuggestedQuestions(parsedQuestions);
    } catch (err) {
      console.error(" Image processing failed:", err);
      setError("Failed to process image. Try another one.");
    } finally {
      setLoading(false);
    }
  };

  const resetUploader = () => {
    setImageName("");
    setError("");
    setSuggestedQuestions([]);
    resetChangeTrigger(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  useEffect(() => {
    if (isResetImage) {
      resetUploader();
    }
  }, [isResetImage]);

  return (
    <div>
      <div
        style={{ display: "flex", }}
      >
        <div onClick={handleIconClick} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
          <Icon name="file_image" themedColor={imageName && !loading ? "#ea1b0a" : "#cccbca"} />
          {/* <span>{imageName || t("chatBot.imageUpload")}</span> */}
          <EonUiTooltip slot="tooltip" size="small" placement="top" >
            <div style={{ fontSize: "12px", lineHeight: "1.6" }}>
              {t("chatBot.uploadImageTooltip")}
              <br />
              {t("chatBot.uploadImageFormat")}
            </div>
          </EonUiTooltip>
        </div>
        {imageName && !loading && (
          <span style={{ cursor: "pointer" }} onClick={resetUploader}>
            <Icon name="cross" themedColor={"#ea1b0a"} />
          </span>
        )}
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept=".jpg, .jpeg, .png"
      />

      {loading && <p style={{ color: "#666" }}>{t("chatBot.uploadImageProcessing")}</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
  );
};

export default ImageUploader;